import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import api from '@/api'
import { Vue } from 'vue-property-decorator'
import { ProductApiModel } from '@/interfaces/ProductApiModel'
import { ConfigurationApiModel } from '@/interfaces/ConfigurationApiModel'
import lodash from 'lodash'
import { SummarySection } from '@/interfaces/SummarySection'

@Module({ namespaced: true })
class CurrentModel extends VuexModule {
  public model: ProductApiModel = {
    id: 0,
    description: '',
    code: '',
    configurator: {
      configuration: '',
      width: 0,
      depth: '21.5',
      height: 0,
      panelFinishCategory: '',
      panelFinish: '',
      metalFinishCategory: '',
      metalFinish: '',
      countertopCategory: '',
      countertop: '',
      edgeProfile: '',
      overhang: '',
      faucet: '',
      sink: '',
      hardware: '',
      sinkOffset: 0,
      doubleSink: 0,
      faucetStyle: '',
      base: ''
    },
    name: '',
    isDraft: false,
    hasMetalFrame: false,
    hasLegs: false,
    hasConfigurationLegs: false,
    defaultEdgeProfile: '',
    availableDepths: ['18', '21.5'],
    seq: 0,
    productPageUrl: '',
    sizeItemNumber: ''
  }

  public viewerFrame = 1;

  public configurationKey?: string = undefined

  public priceSections: Array<SummarySection> = []

  public fetchedData = false

  public isBeingUpdated = false

  @Mutation
  public setViewerFrame(frame: number): void {
    if (frame !== undefined) {
      this.viewerFrame = frame
    }
  }

  @Mutation
  public setModelConfiguration(payload: ConfigurationApiModel): void {
    if (payload !== undefined) {
      Object.assign(this.model.configurator, payload)
    }
  }

  @Action
  public updateModelConfiguration(payload: ConfigurationApiModel): void {
    this.context.commit('setModelConfiguration', payload)
  }

  @Mutation
  public setModelProperty(payload: { property: string; value: any }): void {
    Vue.set(this.model.configurator, payload.property, payload.value)
  }

  @Action
  public updateModelProperty(payload: { property: string; value: any }): void {
    this.context.commit('setModelProperty', payload)
  }

  @Action
  public updateViewerFrame(frame: number): void {
    this.context.commit('setViewerFrame', frame)
  }

  @Mutation
  public setModelPropertyOption(payload: { property: string; value: string; action: string }): void {
    let propertyOptionArray = lodash.get(this.model.configurator, payload.property) as Array<string>
    if (!propertyOptionArray) {
      propertyOptionArray = []
      Vue.set(this.model.configurator, payload.property, propertyOptionArray)
    }
    const optionIndex = propertyOptionArray.indexOf(payload.value)
    if (optionIndex < 0 && payload.action !== 'remove') {
      propertyOptionArray.push(payload.value)
    } else if (optionIndex >= 0 && payload.action !== 'add') {
      propertyOptionArray.splice(optionIndex, 1)
    }
  }

  @Action
  public addModelPropertyOption(payload: { property: string; value: string }): void {
    this.context.commit('setModelPropertyOption', { ...payload, ...{ action: 'add' } })
  }

  @Action
  public removeModelPropertyOption(payload: { property: string; value: string }): void {
    this.context.commit('setModelPropertyOption', { ...payload, ...{ action: 'remove' } })
  }

  @Action
  public toggleModelPropertyOption(payload: { property: string; value: string }): void {
    this.context.commit('setModelPropertyOption', { ...payload, ...{ action: 'toggle' } })
  }

  @Mutation
  public saveModel(payload: { data: ProductApiModel; force: boolean }): void {
    if (this.fetchedData && !payload.force) {
      this.model.id = payload.data.id
      this.model.description = payload.data.description
      this.model.code = payload.data.code
      this.model.name = payload.data.name
      this.model.hasMetalFrame = payload.data.hasMetalFrame
      this.model.hasLegs = payload.data.hasLegs
      this.model.hasConfigurationLegs = payload.data.hasConfigurationLegs
      this.model.defaultEdgeProfile = payload.data.defaultEdgeProfile
      this.model.availableDepths = payload.data.availableDepths
      this.model.productPageUrl = payload.data.productPageUrl
      this.model.sizeItemNumber = payload.data.sizeItemNumber
    } else {
      this.fetchedData = true
      this.model = payload.data
    }

    if (window.configurationKey) {
      this.configurationKey = window.configurationKey

      delete window.configurationKey
    }

    if (window.configurationDesign) {
      const design = window.configurationDesign
      const keys = Object.keys(design)
      keys.forEach(key => {
        Vue.set(this.model.configurator, key, design[key])
      })
    }
    delete window.configurationDesign
  }

  @Action
  public loadModel(productCode: string): Promise<boolean> {
    if (this.fetchedData && this.model.code === productCode) {
      return Promise.resolve(true)
    } else {
      return api
        .get('/products/' + productCode)
        .then(response => {
          if (response.data) {
            this.context.commit('saveModel', { data: response.data, force: false })
            return true
          } else {
            return false
          }
        })
        .catch(() => {
          return false
        })
    }
  }

  @Action
  public setModel(data: ProductApiModel): void {
    this.context.commit('saveModel', { data, force: true })
  }

  @Mutation
  public setPriceItems(payload: { priceSections: Array<SummarySection> }): void {
    this.priceSections = payload.priceSections
  }

  @Action
  public updatePriceItems(payload: { priceSections: Array<SummarySection> }): void {
    this.context.commit('setPriceItems', payload)
  }

  get totalPrice(): number {
    return lodash.sumBy(this.priceSections, 'totalPrice')
  }

  @Mutation
  public saveConfigurationKey(code?: string): void {
    this.configurationKey = code
  }

  @Action
  public clearConfigurationKey(): void {
    this.context.commit('saveConfigurationKey', undefined)
  }

  @Action
  public saveConfiguration(): Promise<boolean> {
    return api
      .post('/products/' + this.model.code + '/configurations/', this.model.configurator)
      .then(response => {
        this.context.commit('saveConfigurationKey', response.data.key)
        return true
      })
      .catch(() => {
        return false
      })
  }

  @Action
  public toggleUpdating(isBeingUpdated: boolean) {
    this.context.commit('setIsBeingUpdate', isBeingUpdated)
  }

  @Mutation
  public setIsBeingUpdate(isBeingUpdated: boolean): void {
    this.isBeingUpdated = isBeingUpdated
  }
}
export default CurrentModel
